import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Checkbox,
  OutlinedInput,
  Snackbar,
  Tooltip,
  Collapse,
  Menu,
  Autocomplete,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_URL } from './constants';
import {
  sanitizeVariant,
  extractVariantsFromRaw,
  getMatchingStorageKey,
  mergeReferenceTablesForCountries,
} from './matchingUtils';
import { resolveProductImageUrlFromFields } from './productUtils';
import ProductDetailPopup from './ProductDetailPopup';
import { ProductImageHoverOverlay } from './ProductImageHoverOverlay';
import useSkuTitleTranslations from './hooks/useSkuTitleTranslations';
import SkuTitleWithTranslate from './components/SkuTitleWithTranslate';

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Default group name from SKU (e.g. "IQOS ILUMA i - Midnight Black" → "IQOS ILUMA i"). */
function defaultGroupNameFromSku(sku) {
  if (!sku || typeof sku !== 'string') return sku || '';
  const t = sku.trim();
  const dash = t.indexOf(' - ');
  if (dash > 0) return t.slice(0, dash).trim();
  const paren = t.indexOf(' (');
  if (paren > 0) return t.slice(0, paren).trim();
  return t;
}

function MatchingSkuHoverWrap({ productImage, productName, children, sx }) {
  const [cursorPos, setCursorPos] = useState(null);
  return (
    <>
      <Box
        onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setCursorPos(null)}
        sx={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%', alignItems: 'center', ...sx }}
      >
        {children}
      </Box>
      <ProductImageHoverOverlay
        cursorPos={cursorPos}
        productImage={productImage}
        productName={productName}
      />
    </>
  );
}

function skuRowTime(item) {
  const raw = item?.updated_at || item?.date;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function ProductMatchingView() {
  const { projectKey } = useParams();
  const {
    getDisplayTitle: getSkuDisplayTitle,
    isTranslationActive: isSkuTranslationActive,
    isTranslatingTitle: isSkuTitleTranslating,
    toggleSkuTitleTranslation,
  } = useSkuTitleTranslations();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [retailer, setRetailer] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [refTable, setRefTable] = useState({ groups: [], modelGroups: [] });
  const [categoryOverrides, setCategoryOverrides] = useState({});
  const [serverTables, setServerTables] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [variantInputByGroup, setVariantInputByGroup] = useState({}); // key: groupId or groupId-vgId
  const [newVariantGroupNameByGroup, setNewVariantGroupNameByGroup] = useState({}); // groupId -> name for new variant group
  const [variantsExpandedByGroup, setVariantsExpandedByGroup] = useState({}); // groupId -> boolean, default collapsed
  /** When true, "+ Add SKU" lists every SKU not already in that group (can be in other groups). */
  const [allowSkuInMultipleGroups, setAllowSkuInMultipleGroups] = useState(false);
  /** Click a SKU chip label → pick another group to add the same SKU (multi-group). */
  const [skuAddToGroupMenu, setSkuAddToGroupMenu] = useState(null);
  const [newModelGroupName, setNewModelGroupName] = useState('');
  const [productFlags, setProductFlags] = useState({});
  const [packSizeOverrides, setPackSizeOverrides] = useState({});
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [productPopupData, setProductPopupData] = useState({
    productName: null,
    productImage: null,
    productData: null,
    productDataRows: [],
    groupProductNames: undefined,
  });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dataResponse, overridesResponse, matchingResponse, flagsResponse, packSizeResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
          axios.get(`${API_URL}/jti/product-flags`).catch(() => ({ data: { flags: {} } })),
          axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } })),
        ]);
        const { columns: apiColumns, data: apiData } = dataResponse.data;
        const processed = apiData.map((row) => {
          const obj = {};
          apiColumns.forEach((col, index) => { obj[col] = row[index]; });
          return obj;
        });
        setData(processed);
        setCategoryOverrides(overridesResponse?.data?.overrides || {});
        setServerTables(matchingResponse?.data?.tables || {});
        setProductFlags(flagsResponse?.data?.flags || {});
        setPackSizeOverrides(packSizeResponse?.data?.overrides || {});
      } catch (err) {
        console.error('Error fetching matching data:', err);
        setError('Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setVariantsExpandedByGroup({});
  }, [selectedCountries, retailer, categoryFilter]);

  const getEffectiveCategoryGroup = (item) => {
    if (!item) return '';
    const title = item.title;
    return (title && categoryOverrides?.[title]) || item.category_group || '';
  };

  const countries = useMemo(() => {
    return [...new Set(data.map((r) => r.country).filter(Boolean))].sort();
  }, [data]);

  const retailers = useMemo(() => {
    if (selectedCountries.length === 0) return [...new Set(data.map((r) => r.retailer).filter(Boolean))].sort();
    return [...new Set(data.filter((r) => selectedCountries.includes(r.country)).map((r) => r.retailer).filter(Boolean))].sort();
  }, [data, selectedCountries]);

  const categoryOptions = useMemo(() => {
    let rows = data;
    if (selectedCountries.length > 0) rows = rows.filter((r) => selectedCountries.includes(r.country));
    if (retailer) rows = rows.filter((r) => r.retailer === retailer);
    return [...new Set(rows.map((r) => getEffectiveCategoryGroup(r)).filter(Boolean))].sort();
  }, [data, selectedCountries, retailer, categoryOverrides]);

  // Get SKUs from selected countries and retailer, deduplicated by title
  const skusForSelection = useMemo(() => {
    if (selectedCountries.length === 0 || !retailer) return [];
    let rows = data.filter((r) => selectedCountries.includes(r.country) && r.retailer === retailer);
    if (categoryFilter) rows = rows.filter((r) => getEffectiveCategoryGroup(r) === categoryFilter);
    
    // Deduplicate by title - each product title should appear only once
    const titleMap = new Map();
    rows.forEach((r) => {
      if (!r.title) return;
      if (!titleMap.has(r.title)) {
        // Store first occurrence, collect all countries where this product appears
        titleMap.set(r.title, {
          title: r.title,
          countries: [r.country],
          retailer: r.retailer
        });
      } else {
        // Add country to existing entry if not already present
        const existing = titleMap.get(r.title);
        if (!existing.countries.includes(r.country)) {
          existing.countries.push(r.country);
        }
      }
    });
    
    return Array.from(titleMap.values())
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [data, selectedCountries, retailer, categoryFilter, categoryOverrides]);

  // Check if SKU is assigned (considering cross-country groups)
  const assignedSkus = useMemo(() => {
    const set = new Set();
    refTable.groups.forEach((g) => {
      g.skus.forEach((sku) => {
        // SKU is stored as string (title)
        const skuKey = typeof sku === 'string' ? sku : sku.title;
        if (skuKey) set.add(skuKey);
      });
    });
    return set;
  }, [refTable]);

  // Get category groups for a group based on its SKUs
  const getGroupCategoryGroups = useCallback((group) => {
    if (!group || !group.skus || group.skus.length === 0) return [];
    const categoryGroups = new Set();
    group.skus.forEach((sku) => {
      const skuTitle = typeof sku === 'string' ? sku : sku.title;
      if (!skuTitle) return;
      // Find all products with this title (across all countries and retailers)
      // to get all category groups for this SKU
      const products = data.filter((r) => 
        r.title === skuTitle && 
        (selectedCountries.length === 0 || selectedCountries.includes(r.country)) &&
        (!retailer || r.retailer === retailer)
      );
      products.forEach((product) => {
        const categoryGroup = getEffectiveCategoryGroup(product);
        if (categoryGroup) categoryGroups.add(categoryGroup);
      });
    });
    return Array.from(categoryGroups).sort();
  }, [data, selectedCountries, retailer, categoryOverrides]);

  // Filter groups by category if filter is set
  const filteredGroups = useMemo(() => {
    if (!categoryFilter) return refTable.groups;
    return refTable.groups.filter((group) => {
      const groupCategoryGroups = getGroupCategoryGroups(group);
      return groupCategoryGroups.includes(categoryFilter);
    });
  }, [refTable.groups, categoryFilter, getGroupCategoryGroups]);

  /** Existing model-group entries (explicit list + names derived from matching-group membership). */
  const modelGroupsList = useMemo(() => {
    const byName = new Map();
    (refTable.modelGroups || []).forEach((mg) => {
      const name = (mg?.name || '').trim();
      if (!name) return;
      byName.set(name, { id: mg.id || name, name });
    });
    (refTable.groups || []).forEach((g) => {
      const name = (g.modelGroup || '').trim();
      if (!name || byName.has(name)) return;
      byName.set(name, { id: generateId(), name });
    });
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [refTable.modelGroups, refTable.groups]);

  /** Matching groups (models) available to assign into a model series. */
  const matchingModelsForSelect = useMemo(
    () =>
      (refTable.groups || [])
        .filter((g) => (g.name || '').trim())
        .map((g) => ({ id: g.id, name: String(g.name).trim() }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [refTable.groups]
  );

  const unassignedSkus = useMemo(() => {
    return skusForSelection.filter((item) => !assignedSkus.has(item.title));
  }, [skusForSelection, assignedSkus]);

  const getSkuRows = useCallback(
    (title, countries) => {
      if (!title) return [];
      let rows = data.filter((r) => r.title === title);
      if (selectedCountries.length) {
        rows = rows.filter((r) => selectedCountries.includes(r.country));
      }
      if (retailer) {
        rows = rows.filter((r) => r.retailer === retailer);
      }
      if (countries?.length) {
        const narrowed = rows.filter((r) => countries.includes(r.country));
        if (narrowed.length) rows = narrowed;
      }
      return rows.map((row) => ({
        ...row,
        category_group: (row.title && categoryOverrides?.[row.title]) || row.category_group,
      }));
    },
    [data, selectedCountries, retailer, categoryOverrides]
  );

  const getSkuPreview = useCallback(
    (title, countries) => {
      const rows = getSkuRows(title, countries);
      const primary = rows.length
        ? rows.reduce((best, row) => (skuRowTime(row) > skuRowTime(best) ? row : best), rows[0])
        : null;
      const image = primary
        ? resolveProductImageUrlFromFields(
            primary.image,
            primary.box_image_urls,
            primary.image_urls,
            primary.media_gallery_urls
          )
        : null;
      return { rows, primary, image };
    },
    [getSkuRows]
  );

  const openProductPopup = useCallback(
    (title, countries) => {
      if (!title) return;
      const { rows, primary, image } = getSkuPreview(title, countries);
      setProductPopupData({
        productName: title,
        productImage: image,
        productData: primary,
        productDataRows: rows,
        groupProductNames: undefined,
      });
      setProductPopupOpen(true);
    },
    [getSkuPreview]
  );

  const getSkusSelectableForGroupDropdown = (group) => {
    if (allowSkuInMultipleGroups) {
      const inGroup = new Set(
        (group?.skus || []).map((s) => (typeof s === 'string' ? s : s.title || s)).filter(Boolean)
      );
      return skusForSelection.filter((item) => item.title && !inGroup.has(item.title));
    }
    return unassignedSkus;
  };

  const skuTitleFromRefEntry = (skuEntry) =>
    (typeof skuEntry === 'string' ? skuEntry : skuEntry?.title || skuEntry || '').trim();

  /** How many matching groups include this SKU title (for multi-group chip styling). */
  const skuTitleToGroupCount = useMemo(() => {
    const counts = new Map();
    refTable.groups.forEach((g) => {
      (g.skus || []).forEach((s) => {
        const t = skuTitleFromRefEntry(s);
        if (!t) return;
        counts.set(t, (counts.get(t) || 0) + 1);
      });
    });
    return counts;
  }, [refTable.groups]);

  const otherGroupsForSkuMultiAdd = useMemo(() => {
    if (!skuAddToGroupMenu) return [];
    const t = skuTitleFromRefEntry(skuAddToGroupMenu.sku);
    if (!t) return [];
    return refTable.groups.filter((g) => {
      if (g.id === skuAddToGroupMenu.sourceGroupId) return false;
      return !(g?.skus || []).some((s) => skuTitleFromRefEntry(s) === t);
    });
  }, [skuAddToGroupMenu, refTable.groups]);

  useEffect(() => {
    if (selectedCountries.length > 0 && retailer) {
      setRefTable(mergeReferenceTablesForCountries(serverTables, selectedCountries, retailer));
    } else {
      setRefTable({ groups: [], modelGroups: [] });
    }
  }, [selectedCountries, retailer, serverTables]);

  const persistRefTable = (next) => {
    const payload = {
      groups: next.groups || [],
      modelGroups: Array.isArray(next.modelGroups) ? next.modelGroups : (refTable.modelGroups || []),
    };
    setRefTable(payload);
    if (selectedCountries.length > 0 && retailer) {
      selectedCountries.forEach((country) => {
        const key = getMatchingStorageKey(country, retailer);
        setServerTables((prev) => ({ ...prev, [key]: payload }));
        axios.post(`${API_URL}/jti/matching-groups`, { key, data: payload })
          .catch((err) => console.warn('Failed to sync matching groups to server:', err));
      });
    }
  };

  const addGroup = () => {
    persistRefTable({
      ...refTable,
      groups: [...refTable.groups, { id: generateId(), name: '', modelGroup: '', skus: [], variantGroups: [] }],
    });
  };

  const updateGroupName = (groupId, name) => {
    persistRefTable({
      ...refTable,
      groups: refTable.groups.map((g) => (g.id === groupId ? { ...g, name } : g)),
    });
  };

  const addModelGroup = () => {
    const name = newModelGroupName.trim();
    if (!name) return;
    if (modelGroupsList.some((m) => m.name.toLowerCase() === name.toLowerCase())) {
      showSnackbar('Model series already exists', 'warning');
      return;
    }
    persistRefTable({
      ...refTable,
      modelGroups: [...(refTable.modelGroups || []), { id: generateId(), name }],
    });
    setNewModelGroupName('');
  };

  const updateModelGroupName = (modelGroup, nextName) => {
    const oldName = modelGroup.name;
    const next = String(nextName ?? '');
    if (next === oldName) return;
    const existing = refTable.modelGroups || [];
    const hasEntry = existing.some(
      (m) => m.id === modelGroup.id || (m.name || '').trim() === oldName.trim()
    );
    const nextModelGroups = hasEntry
      ? existing.map((m) =>
          m.id === modelGroup.id || (m.name || '').trim() === oldName.trim()
            ? { ...m, id: m.id || modelGroup.id, name: next }
            : m
        )
      : [...existing, { id: modelGroup.id || generateId(), name: next }];
    persistRefTable({
      modelGroups: nextModelGroups,
      groups: refTable.groups.map((g) =>
        (g.modelGroup || '').trim() === oldName.trim()
          ? { ...g, modelGroup: next.trim() }
          : g
      ),
    });
  };

  const setModelGroupMembers = (modelGroupName, selectedModelIds) => {
    const name = String(modelGroupName || '').trim();
    if (!name) return;
    const idSet = new Set(selectedModelIds || []);
    persistRefTable({
      ...refTable,
      groups: refTable.groups.map((g) => {
        const shouldBeIn = idSet.has(g.id);
        const wasIn = (g.modelGroup || '').trim() === name;
        if (shouldBeIn) return { ...g, modelGroup: name };
        if (wasIn) return { ...g, modelGroup: '' };
        return g;
      }),
    });
  };

  const removeModelGroup = (modelGroupName) => {
    persistRefTable({
      modelGroups: (refTable.modelGroups || []).filter(
        (m) => (m.name || '').trim() !== modelGroupName
      ),
      groups: refTable.groups.map((g) =>
        (g.modelGroup || '').trim() === modelGroupName ? { ...g, modelGroup: '' } : g
      ),
    });
  };

  const removeGroup = (groupId) => {
    persistRefTable({
      ...refTable,
      groups: refTable.groups.filter((g) => g.id !== groupId),
    });
  };

  const addSkuToGroup = (groupId, skuItem) => {
    // Store as string (title only) for per-country/retailer storage
    // skuItem can be string or object with title/countries
    const skuTitle = typeof skuItem === 'string' ? skuItem : (skuItem.title || skuItem);
    
    persistRefTable({
      groups: refTable.groups.map((g) => {
        if (g.id !== groupId) return g;
        // Check if SKU already exists
        const exists = g.skus.some((s) => {
          const sTitle = typeof s === 'string' ? s : (s.title || s);
          return sTitle === skuTitle;
        });
        if (exists) return g;
        return { ...g, skus: [...g.skus, skuTitle] };
      })
    });
  };

  const removeSkuFromGroup = (groupId, skuItem) => {
    // skuItem can be string or object with title/countries
    const skuTitle = typeof skuItem === 'string' ? skuItem : (skuItem.title || skuItem);
    persistRefTable({
      groups: refTable.groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          skus: g.skus.filter((s) => {
            const sTitle = typeof s === 'string' ? s : (s.title || s);
            return sTitle !== skuTitle;
          })
        };
      })
    });
  };

  const assignUnassignedToGroup = (sku, groupId) => {
    if (!groupId) return;
    addSkuToGroup(groupId, sku);
  };

  const makeGroupFromSku = (skuItem) => {
    const skuTitle = typeof skuItem === 'string' ? skuItem : (skuItem.title || skuItem);
    persistRefTable({
      groups: [
        ...refTable.groups,
        { id: generateId(), name: defaultGroupNameFromSku(skuTitle), modelGroup: '', skus: [skuTitle], variantGroups: [] }
      ]
    });
  };

  const addVariantGroup = (groupId, groupName = '') => {
    const group = refTable.groups.find((g) => g.id === groupId);
    const vgs = group?.variantGroups || [];
    if (vgs.some((vg) => (vg.name || '') === (groupName || ''))) return;
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId ? { ...g, variantGroups: [...(g.variantGroups || []), { id: generateId(), name: String(groupName || '').trim(), variants: [], disabledVariants: [] }] } : g
      )
    });
  };

  const removeVariantGroup = (groupId, vgId) => {
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId ? { ...g, variantGroups: (g.variantGroups || []).filter((vg) => vg.id !== vgId) } : g
      )
    });
  };

  const addVariantToVariantGroup = (groupId, vgId, variant) => {
    const v = sanitizeVariant(variant);
    if (!v) return;
    const group = refTable.groups.find((g) => g.id === groupId);
    const vg = (group?.variantGroups || []).find((x) => x.id === vgId);
    if (!vg || (vg.variants || []).includes(v)) return;
    const inOtherVg = (group?.variantGroups || []).some((x) => x.id !== vgId && (x.variants || []).includes(v));
    if (inOtherVg) {
      showSnackbar(`"${v}" is already in another variant group`, 'warning');
      return;
    }
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              variantGroups: (g.variantGroups || []).map((x) =>
                x.id === vgId ? { ...x, variants: [...(x.variants || []), v].sort() } : x
              )
            }
          : g
      )
    });
  };

  const removeVariantFromVariantGroup = (groupId, vgId, variant) => {
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              variantGroups: (g.variantGroups || []).map((x) =>
                x.id === vgId ? { ...x, variants: (x.variants || []).filter((v) => v !== variant) } : x
              )
            }
          : g
      )
    });
  };

  const disableVariantInGroup = (groupId, vgId, variant) => {
    const group = refTable.groups.find((g) => g.id === groupId);
    const vg = (group?.variantGroups || []).find((x) => x.id === vgId);
    if (!vg || !(vg.variants || []).includes(variant)) return;
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              variantGroups: (g.variantGroups || []).map((x) =>
                x.id === vgId
                  ? {
                      ...x,
                      variants: (x.variants || []).filter((v) => v !== variant),
                      disabledVariants: [...(x.disabledVariants || []), variant].sort()
                    }
                  : x
              )
            }
          : g
      )
    });
  };

  const reEnableVariantInGroup = (groupId, vgId, variant) => {
    const group = refTable.groups.find((g) => g.id === groupId);
    const vg = (group?.variantGroups || []).find((x) => x.id === vgId);
    if (!vg) return;
    const inOtherVg = (group?.variantGroups || []).some((x) => x.id !== vgId && (x.variants || []).includes(variant));
    if (inOtherVg) {
      showSnackbar(`"${variant}" is already in another variant group`, 'warning');
      return;
    }
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              variantGroups: (g.variantGroups || []).map((x) =>
                x.id === vgId
                  ? {
                      ...x,
                      variants: [...(x.variants || []), variant].sort(),
                      disabledVariants: (x.disabledVariants || []).filter((v) => v !== variant)
                    }
                  : x
              )
            }
          : g
      )
    });
  };

  const makeVariantGroupFromVariant = (groupId, vgId, variant) => {
    const group = refTable.groups.find((g) => g.id === groupId);
    const vg = (group?.variantGroups || []).find((x) => x.id === vgId);
    if (!vg || !(vg.variants || []).includes(variant)) return;
    const updatedVgs = (group.variantGroups || []).map((x) =>
      x.id === vgId ? { ...x, variants: (x.variants || []).filter((v) => v !== variant) } : x
    );
    const newVg = { id: generateId(), name: variant, variants: [variant], disabledVariants: [] };
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId ? { ...g, variantGroups: [...updatedVgs, newVg] } : g
      )
    });
  };

  const moveVariantToVariantGroup = (groupId, fromVgId, toVgId, variant) => {
    if (fromVgId === toVgId) return;
    const group = refTable.groups.find((g) => g.id === groupId);
    const fromVg = (group?.variantGroups || []).find((x) => x.id === fromVgId);
    const toVg = (group?.variantGroups || []).find((x) => x.id === toVgId);
    if (!fromVg || !toVg || !(fromVg.variants || []).includes(variant)) return;
    if ((toVg.variants || []).includes(variant)) return;
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              variantGroups: (g.variantGroups || []).map((x) => {
                if (x.id === fromVgId) return { ...x, variants: (x.variants || []).filter((v) => v !== variant) };
                if (x.id === toVgId) return { ...x, variants: [...(x.variants || []), variant].sort() };
                return x;
              })
            }
          : g
      )
    });
  };

  const updateVariantGroupName = (groupId, vgId, name) => {
    persistRefTable({
      groups: refTable.groups.map((g) =>
        g.id === groupId
          ? { ...g, variantGroups: (g.variantGroups || []).map((x) => (x.id === vgId ? { ...x, name: String(name || '').trim() } : x)) }
          : g
      )
    });
  };

  // Infer variants from data (variant, product_variants, product_variant_names) + from SKU names
  const getInferredVariantsForGroup = useCallback((group) => {
    if (!group?.skus?.length) return [];
    const seen = new Set();
    const groupName = (group.name || '').trim();
    const skuTitles = group.skus.map((s) => (typeof s === 'string' ? s : s.title || s)).filter(Boolean);

    // From data: variant, product_variants, product_variant_names
    data.filter((r) => skuTitles.includes(r.title)).forEach((item) => {
      [item.variant, item.product_variants, item.product_variant_names].forEach((raw) => {
        extractVariantsFromRaw(raw).forEach((v) => seen.add(v));
      });
    });

    // From SKU names: "Ploom X Black" -> "Black", "Ploom X Ruby Twist" -> "Ruby Twist" (keep multi-word flavours together)
    // Only split on explicit separators ( - , / , & ), not on spaces, so "Ruby Twist" stays one variant
    skuTitles.forEach((sku) => {
      let rest = sku;
      if (groupName) {
        const escaped = groupName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        rest = sku.replace(new RegExp(`^${escaped}\\s*[-–—]?\\s*`, 'i'), '').trim();
        if (rest === sku) rest = sku.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), '').trim();
      }
      if (rest && rest.length > 1) {
        const parts = rest.split(/\s*[-–—/&]\s*/);
        parts.forEach((p) => {
          const t = sanitizeVariant(p);
          if (t && t.length > 1 && !/^\d+$/.test(t)) seen.add(t);
        });
      }
    });

    return [...seen].sort();
  }, [data]);

  const applyAutoInferredVariants = (groupId) => {
    const group = refTable.groups.find((g) => g.id === groupId);
    if (!group) return;
    const inferred = getInferredVariantsForGroup(group);
    const vgs = group.variantGroups || [];
    const firstVg = vgs[0];
    const disabledSet = new Set(firstVg?.disabledVariants || []);
    const inOtherVgs = new Set(vgs.slice(1).flatMap((x) => x.variants || []));
    const filteredInferred = inferred.filter((v) => !disabledSet.has(v) && !inOtherVgs.has(v));
    if (firstVg) {
      const merged = [...new Set([...(firstVg.variants || []), ...filteredInferred])].sort();
      persistRefTable({
        groups: refTable.groups.map((g) =>
          g.id === groupId
            ? {
                ...g,
                variantGroups: (g.variantGroups || []).map((x) =>
                  x.id === firstVg.id ? { ...x, variants: merged, disabledVariants: x.disabledVariants || [] } : x
                )
              }
            : g
        )
      });
    } else {
      persistRefTable({
        groups: refTable.groups.map((g) =>
          g.id === groupId
            ? { ...g, variantGroups: [{ id: generateId(), name: '', variants: filteredInferred, disabledVariants: [] }] }
            : g
        )
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Product Matching
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define groups (same product / model, different variant), assign SKUs, and specify variants (colours for devices, flavours for sticks). Use Model Series below to link related models (e.g. IQOS Iluma i and IQOS Iluma i One). Products in matching groups auto-inherit variants. Changes are saved to the server and synced whenever the dataset loads.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="matching-countries-label">Countries</InputLabel>
              <Select
                labelId="matching-countries-label"
                id="matching-countries"
                multiple
                value={selectedCountries}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
                  setRetailer('');
                }}
                input={<OutlinedInput label="Countries" />}
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) return <em>Select countries</em>;
                  return selected.join(', ');
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Checkbox checked={selectedCountries.indexOf(c) > -1} />
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="matching-retailer-label">Retailer</InputLabel>
              <Select
                labelId="matching-retailer-label"
                id="matching-retailer"
                value={retailer}
                label="Retailer"
                onChange={(e) => setRetailer(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select retailer</em>
                </MenuItem>
                {retailers.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="matching-category-label">Category group</InputLabel>
              <Select
                labelId="matching-category-label"
                id="matching-category"
                value={categoryFilter}
                label="Category group"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {categoryOptions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {selectedCountries.length === 0 || !retailer ? (
        <Alert severity="info">
          Select one or more countries and a retailer to view and edit the reference table. You can match products across multiple countries in the same group.
        </Alert>
      ) : (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  Groups — same product, different variant
                </Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addGroup}>
                  Add group
                </Button>
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedCountries.join(', ')} / {retailer} — {skusForSelection.length} SKU(s) in data, {unassignedSkus.length} unassigned
                </Typography>
                <Tooltip
                  title={
                    allowSkuInMultipleGroups
                      ? 'On: "+ Add SKU" can attach SKUs that are already in another group. Off: only unassigned SKUs (default).'
                      : 'Off: each SKU can only be added from unassigned. Turn on to add the same SKU to another group.'
                  }
                >
                  <IconButton
                    size="small"
                    color={allowSkuInMultipleGroups ? 'primary' : 'default'}
                    onClick={() => setAllowSkuInMultipleGroups((v) => !v)}
                    aria-label={allowSkuInMultipleGroups ? 'Multi-group add on' : 'Multi-group add off'}
                    aria-pressed={allowSkuInMultipleGroups}
                    sx={{ p: 0.5 }}
                  >
                    <LayersOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {filteredGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {categoryFilter 
                    ? `No groups found for category "${categoryFilter}".`
                    : 'No groups yet. Click "Add group" to create one, then assign SKUs from the list below or from unassigned SKUs.'}
                </Typography>
              ) : (
                <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {filteredGroups.map((group) => {
                    const groupCategoryGroups = getGroupCategoryGroups(group);
                    return (
                      <Paper key={group.id} variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Model name (matching group)"
                            value={group.name}
                            onChange={(e) => updateGroupName(group.id, e.target.value)}
                            sx={{ minWidth: 260 }}
                          />
                          {(group.modelGroup || '').trim() ? (
                            <Chip
                              label={(group.modelGroup || '').trim()}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              title="Assigned model series"
                            />
                          ) : null}
                          {groupCategoryGroups.length > 0 && (
                            <Chip
                              label={groupCategoryGroups.length === 1 
                                ? groupCategoryGroups[0]
                                : groupCategoryGroups.join(', ')}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeGroup(group.id)}
                            aria-label="Delete group"
                            color="error"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <Select
                            displayEmpty
                            value=""
                            onChange={(e) => {
                              const sku = e.target.value;
                              if (sku) addSkuToGroup(group.id, sku);
                            }}
                            renderValue={() => '+ Add SKU'}
                            sx={{ fontSize: '0.875rem' }}
                          >
                            <MenuItem value="" disabled>
                              <em>+ Add SKU</em>
                            </MenuItem>
                            {getSkusSelectableForGroupDropdown(group).map((skuItem) => {
                              const displayText = selectedCountries.length > 1 && skuItem.countries && skuItem.countries.length > 1
                                ? `${skuItem.title} (${skuItem.countries.join(', ')})`
                                : skuItem.title;
                              return (
                                <MenuItem key={`${skuItem.title}-${skuItem.retailer}`} value={skuItem}>
                                  {displayText}
                                </MenuItem>
                              );
                            })}
                            {getSkusSelectableForGroupDropdown(group).length === 0 && (
                              <MenuItem value="" disabled>
                                {allowSkuInMultipleGroups ? 'No SKUs left to add to this group' : 'All SKUs assigned'}
                              </MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                      {group.skus.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {group.skus.map((sku, idx) => {
                            const skuTitle = typeof sku === 'string' ? sku : sku.title;
                            const groupCount = skuTitleToGroupCount.get(skuTitle) || 0;
                            const isInMultipleGroups = groupCount > 1;
                            const chipTooltip = isInMultipleGroups
                              ? `SKU is in ${groupCount} groups. Click for product details. Right-click to add to another group.`
                              : 'Click for product details. Right-click to add this SKU to another group';
                            const preview = getSkuPreview(skuTitle);
                            return (
                              <MatchingSkuHoverWrap
                                key={`${skuTitle}-${idx}`}
                                productImage={preview.image}
                                productName={skuTitle}
                              >
                                <Tooltip title={chipTooltip}>
                                <Chip
                                  label={skuTitle}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openProductPopup(skuTitle);
                                  }}
                                  onDelete={(e) => {
                                    e.stopPropagation();
                                    removeSkuFromGroup(group.id, sku);
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSkuAddToGroupMenu({
                                      anchorEl: e.currentTarget,
                                      sourceGroupId: group.id,
                                      sku,
                                    });
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    ...(isInMultipleGroups && {
                                      backgroundColor: '#e3f2fd',
                                      borderColor: '#90caf9',
                                      '&:hover': {
                                        backgroundColor: '#bbdefb',
                                      },
                                      '& .MuiChip-deleteIcon': {
                                        color: 'rgba(25, 118, 210, 0.65)',
                                        '&:hover': { color: '#1565c0' },
                                      },
                                    }),
                                  }}
                                />
                                </Tooltip>
                              </MatchingSkuHoverWrap>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No SKUs in this group yet. Use &quot;+ Add SKU&quot; or assign from unassigned list below.
                        </Typography>
                      )}
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                            sx={{ mb: 0.5, cursor: 'pointer' }}
                            onClick={() => setVariantsExpandedByGroup((prev) => ({ ...prev, [group.id]: prev[group.id] !== true }))}
                          >
                            <Typography variant="subtitle2" color="text.secondary">
                              Variants (colours / flavours)
                            </Typography>
                            {variantsExpandedByGroup[group.id] === true ? (
                              <ExpandLessIcon fontSize="small" color="action" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" color="action" />
                            )}
                            {variantsExpandedByGroup[group.id] === true && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => { e.stopPropagation(); applyAutoInferredVariants(group.id); }}
                                disabled={!group.skus?.length}
                              >
                                Auto infer from data
                              </Button>
                            )}
                          </Box>
                          {variantsExpandedByGroup[group.id] !== true ? (
                            <Box sx={{ pl: 0.5 }}>
                              {(group.variantGroups || []).filter((vg) => (vg.variants || []).length > 0).length > 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  {(group.variantGroups || []).filter((vg) => (vg.variants || []).length > 0).map((vg) => vg.name || '(unnamed)').join(', ')}
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  No variant groups yet
                                </Typography>
                              )}
                            </Box>
                          ) : (
                          <Collapse in>
                          {(group.variantGroups || []).map((vg) => (
                            <Box key={vg.id} sx={{ mb: 1.5, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                                <TextField
                                  size="small"
                                  placeholder="Group name (e.g. Colours)"
                                  value={vg.name || ''}
                                  onChange={(e) => updateVariantGroupName(group.id, vg.id, e.target.value)}
                                  sx={{ width: 180 }}
                                />
                                <IconButton size="small" onClick={() => removeVariantGroup(group.id, vg.id)} title="Remove variant group">
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <List dense disablePadding>
                                {(vg.variants || []).map((v) => (
                                  <ListItem key={v} dense disablePadding sx={{ py: 0.25 }}>
                                    <ListItemText primary={v} primaryTypographyProps={{ variant: 'body2' }} />
                                    <Tooltip title="Make variant group (promote to own group)">
                                      <IconButton size="small" onClick={() => makeVariantGroupFromVariant(group.id, vg.id, v)}>
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {(group.variantGroups || []).filter((x) => x.id !== vg.id).length > 0 && (
                                      <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                          displayEmpty
                                          value=""
                                          onChange={(e) => {
                                            const targetVgId = e.target.value;
                                            if (targetVgId) moveVariantToVariantGroup(group.id, vg.id, targetVgId, v);
                                          }}
                                          renderValue={() => 'Move to…'}
                                          sx={{ fontSize: '0.75rem', height: 28 }}
                                        >
                                          <MenuItem value="" disabled><em>Move to…</em></MenuItem>
                                          {(group.variantGroups || []).filter((x) => x.id !== vg.id).map((targetVg) => (
                                            <MenuItem key={targetVg.id} value={targetVg.id}>
                                              {targetVg.name || '(unnamed)'}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                    <Tooltip title="Mark as invalid (excluded from Auto infer)">
                                      <IconButton size="small" onClick={() => disableVariantInGroup(group.id, vg.id, v)}>
                                        <BlockIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <IconButton size="small" onClick={() => removeVariantFromVariantGroup(group.id, vg.id, v)}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </ListItem>
                                ))}
                              </List>
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                                <TextField
                                  size="small"
                                  placeholder="Add variant…"
                                  value={variantInputByGroup[`${group.id}-${vg.id}`] || ''}
                                  onChange={(e) => setVariantInputByGroup((prev) => ({ ...prev, [`${group.id}-${vg.id}`]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addVariantToVariantGroup(group.id, vg.id, variantInputByGroup[`${group.id}-${vg.id}`] || '');
                                      setVariantInputByGroup((prev) => ({ ...prev, [`${group.id}-${vg.id}`]: '' }));
                                    }
                                  }}
                                  sx={{ width: 160 }}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    addVariantToVariantGroup(group.id, vg.id, variantInputByGroup[`${group.id}-${vg.id}`] || '');
                                    setVariantInputByGroup((prev) => ({ ...prev, [`${group.id}-${vg.id}`]: '' }));
                                  }}
                                >
                                  Add
                                </Button>
                              </Box>
                            </Box>
                          ))}
                          <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 1 }}>
                            <TextField
                              size="small"
                              placeholder="New group name (e.g. Colours)"
                              value={newVariantGroupNameByGroup[group.id] || ''}
                              onChange={(e) => setNewVariantGroupNameByGroup((prev) => ({ ...prev, [group.id]: e.target.value }))}
                              sx={{ width: 200 }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                addVariantGroup(group.id, newVariantGroupNameByGroup[group.id] || '');
                                setNewVariantGroupNameByGroup((prev) => ({ ...prev, [group.id]: '' }));
                              }}
                            >
                              Add variant group
                            </Button>
                          </Box>
                          {(group.variantGroups || []).some((vg) => (vg.disabledVariants || []).length > 0) && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Disabled / Invalid variants (excluded from Auto infer)
                              </Typography>
                              <List dense disablePadding>
                                {(group.variantGroups || []).flatMap((vg) =>
                                  (vg.disabledVariants || []).map((v) => (
                                    <ListItem key={`${vg.id}-${v}`} dense disablePadding sx={{ py: 0.25 }}>
                                      <ListItemText
                                        primary={`${v}${vg.name ? ` (${vg.name})` : ''}`}
                                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                      />
                                      <Tooltip title="Re-enable variant">
                                        <IconButton size="small" onClick={() => reEnableVariantInGroup(group.id, vg.id, v)}>
                                          <RestoreIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </ListItem>
                                  ))
                                )}
                              </List>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                            Each variant on its own line. Group by type (e.g. Colours, Flavours). Auto infer uses data columns + SKU names.
                          </Typography>
                          </Collapse>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                <Typography variant="h6">
                  Model Series
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <TextField
                    size="small"
                    placeholder="New model series name"
                    value={newModelGroupName}
                    onChange={(e) => setNewModelGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addModelGroup();
                      }
                    }}
                    sx={{ minWidth: 220 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addModelGroup}
                    disabled={!newModelGroupName.trim()}
                  >
                    Add model series
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Link related models for the Price &amp; Promotions filter tree (e.g. put IQOS Iluma i and IQOS Iluma i One in the same model series).
              </Typography>

              {modelGroupsList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No model series yet. Add one above, then assign matching groups (models) to it.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {modelGroupsList.map((mg) => {
                    const memberModels = matchingModelsForSelect.filter((model) => {
                      const group = refTable.groups.find((g) => g.id === model.id);
                      return (group?.modelGroup || '').trim() === mg.name.trim();
                    });
                    return (
                      <Paper key={mg.id || mg.name} variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
                          <TextField
                            size="small"
                            label="Model series name"
                            value={mg.name}
                            onChange={(e) => updateModelGroupName(mg, e.target.value)}
                            sx={{ minWidth: 220 }}
                          />
                          <Autocomplete
                            multiple
                            size="small"
                            options={matchingModelsForSelect}
                            getOptionLabel={(opt) => opt.name || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={memberModels}
                            onChange={(_, selected) => {
                              setModelGroupMembers(
                                mg.name,
                                selected.map((s) => s.id)
                              );
                            }}
                            sx={{ flex: 1, minWidth: 260 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Models in this series"
                                placeholder={memberModels.length ? '' : 'Select models…'}
                              />
                            )}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeModelGroup(mg.name.trim())}
                            aria-label="Delete model series"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>

          <Menu
            anchorEl={skuAddToGroupMenu?.anchorEl ?? null}
            open={Boolean(skuAddToGroupMenu)}
            onClose={() => setSkuAddToGroupMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            MenuListProps={{ dense: true }}
          >
            <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.8rem' }}>
              Add SKU to another group
            </MenuItem>
            {otherGroupsForSkuMultiAdd.length === 0 ? (
              <MenuItem disabled>
                {refTable.groups.filter((g) => g.id !== skuAddToGroupMenu?.sourceGroupId).length === 0
                  ? 'Add another group first'
                  : 'Already in all other groups'}
              </MenuItem>
            ) : (
              otherGroupsForSkuMultiAdd.map((g) => (
                <MenuItem
                  key={g.id}
                  onClick={() => {
                    if (skuAddToGroupMenu) addSkuToGroup(g.id, skuAddToGroupMenu.sku);
                    setSkuAddToGroupMenu(null);
                  }}
                >
                  {g.name || '(unnamed)'}
                </MenuItem>
              ))
            )}
          </Menu>

          {unassignedSkus.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Unassigned SKUs — assign to a group
                </Typography>
                <List dense disablePadding>
                  {unassignedSkus.map((skuItem) => {
                    const preview = getSkuPreview(skuItem.title, skuItem.countries);
                    return (
                      <ListItem
                        key={`${skuItem.title}-${skuItem.retailer}`}
                        dense
                        secondaryAction={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => makeGroupFromSku(skuItem)}
                            >
                              Make group
                            </Button>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <Select
                                displayEmpty
                                value=""
                                onChange={(e) => assignUnassignedToGroup(skuItem, e.target.value)}
                                renderValue={() => 'Add to group…'}
                                size="small"
                              >
                                <MenuItem value="" disabled>
                                  <em>Add to group…</em>
                                </MenuItem>
                                {refTable.groups.map((g) => (
                                  <MenuItem key={g.id} value={g.id}>
                                    {g.name || '(unnamed)'}
                                  </MenuItem>
                                ))}
                                {refTable.groups.length === 0 && (
                                  <MenuItem value="" disabled>
                                    Add a group first
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Box>
                        }
                      >
                        <MatchingSkuHoverWrap
                          productImage={preview.image}
                          productName={skuItem.title}
                          sx={{ flex: 1, minWidth: 0, pr: 1 }}
                        >
                          <ListItemText
                            primary={
                              <SkuTitleWithTranslate
                                title={skuItem.title}
                                countriesField={skuItem.countries}
                                getDisplayTitle={getSkuDisplayTitle}
                                isTranslationActive={isSkuTranslationActive}
                                isTranslatingTitle={isSkuTitleTranslating}
                                onToggleTranslate={toggleSkuTitleTranslation}
                                onTitleClick={() => openProductPopup(skuItem.title, skuItem.countries)}
                              />
                            }
                            secondary={
                              selectedCountries.length > 1 && skuItem.countries && skuItem.countries.length > 1
                                ? skuItem.countries.join(', ')
                                : undefined
                            }
                            primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                          />
                        </MatchingSkuHoverWrap>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ProductDetailPopup
        open={productPopupOpen}
        onClose={() => setProductPopupOpen(false)}
        productName={productPopupData.productName}
        productImage={productPopupData.productImage}
        productData={productPopupData.productData}
        productDataRows={productPopupData.productDataRows}
        groupProductNames={productPopupData.groupProductNames}
        packSizeOverrides={packSizeOverrides}
        productFlags={productFlags}
        projectKey={projectKey || 'jti-global'}
        country={productPopupData.productData?.country}
        getDisplayTitle={getSkuDisplayTitle}
        isTranslationActive={isSkuTranslationActive}
        isTranslatingTitle={isSkuTitleTranslating}
        onToggleTranslate={toggleSkuTitleTranslation}
      />
    </Box>
  );
}

export default ProductMatchingView;
